import { apiFetch } from "../api/client";
import { uploadPortrait } from "../api/upload";
import { fetchFaces } from "../auth/photo";
import { fetchPhotos } from "./photos";

/**
 * ═══ SA PHOTO, POUR QUE LA RECONNAISSANCE LE TROUVE ═══
 *
 *   POST /v2/domains/:d/faces          api-v1   attache un portrait à soi-même
 *   POST /v2/domains/:d/faces/search   api-v1   lance la recherche
 *
 * Le produit n'a pas de photo de profil : `UserFaces` porte des portraits de référence,
 * que le back office ajoutait seul. Depuis l'album, le participant peut poser le sien —
 * et la reconnaissance a enfin de quoi le retrouver dans les photos de l'événement.
 *
 * ⚠️ POSER LA PHOTO NE SUFFIT PAS, IL FAUT LANCER LA RECHERCHE. Ce sont deux appels, et
 * le second est celui qui coûte : il compare le portrait à toutes les photos de
 * l'événement. La route ne traite QUE la personne qui la demande et ATTEND le résultat
 * (voir `api-v1/routesV2/faces/searchFaces`), pour que l'écran sache quand cesser de
 * tourner.
 *
 * ⚠️ ET ELLE PEUT RENDRE LA MAIN AVANT LA FIN. L'API a trente secondes ; au-delà, le
 * traitement continue de son côté et la réponse dit `done: false`. Ce n'est pas un
 * échec : les photos arriveront, un peu plus tard.
 */

export type SearchOutcome = {
  /** La recherche est allée jusqu'au bout pendant qu'on attendait. */
  done: boolean;
  /** Combien de photos lui ont été rattachées. */
  created: number;
};

/** Attache un portrait à la personne connectée, en remplaçant celui qu'elle avait posé. */
export async function setOwnPortrait(
  domainId: number,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  const fileId = await uploadPortrait(file, signal);
  await apiFetch(`/v2/domains/${domainId}/faces`, {
    method: "POST",
    /* `self` : c'est SA photo, donc elle remplace la sienne et pas celles que
       l'organisateur a ajoutées pour lui. */
    body: { fileId, self: true },
    signal,
  });
}

/**
 * ═══ EFFACER SA PHOTO — C'EST UN DROIT, PAS UN RÉGLAGE ═══
 *
 *   DELETE /v2/domains/:d/faces/:id   api-v1
 *
 * Un portrait est une donnée biométrique : la personne qui l'a posé doit pouvoir le
 * retirer, sans écrire à l'organisateur. La route efface la ligne `UserFaces` et le
 * fichier du seau (`api-v1`, `api/routesV2/faces/removeFace`) : la photo disparaît de
 * l'écran, et plus rien ici ne la montre.
 *
 * ⚠️ LA RECONNAISSANCE, ELLE, S'ARRÊTE VRAIMENT : `searchUserFace` part des lignes
 * `UserFace` (`db.UserFace.findAll`), donc plus de ligne, plus de recherche lancée sur
 * cette personne. C'est ce que promet la question posée avant l'effacement.
 *
 * ⚠️ MAIS L'EMPREINTE RESTE STOCKÉE DANS REKOGNITION, et ce n'est pas cet écran qui peut
 * la retirer. `removeFace` importe `deleteFace` sans jamais l'appeler — là où `addFace`
 * l'appelle bien quand il remplace un portrait. Plus rien ne s'en sert, mais une donnée
 * biométrique survit à sa suppression dans la collection du domaine : pour le RGPD, c'est
 * l'effacement qui est incomplet, pas l'affichage. Le geste manquant est dans `api-v1`
 * (`deleteFace(domainId, userFace.faceId)` dans la route) — signalé plutôt que fait ici,
 * un sujet par dépôt.
 *
 * ⚠️ TOUS SES VISAGES, PAS SEULEMENT LE DERNIER. L'écran en montre UN (le plus récent) ;
 * une personne peut en porter plusieurs, posés au fil des ans ou ajoutés par le back
 * office depuis sa fiche. N'effacer que celui qu'on voit rendrait « Supprimer ma photo »
 * mensonger : la pastille se rechargerait aussitôt avec le portrait d'en dessous, et la
 * reconnaissance continuerait de tourner sur les autres.
 *
 * ⚠️ CE QUI RESTE, ET IL FAUT LE DIRE : les photos de l'événement déjà rattachées à
 * quelqu'un le restent. Ce sont des photos prises par l'organisateur, pas les siennes ;
 * les détacher est un autre geste, qui n'appartient pas à cet écran.
 *
 * ⚠️ LA ROUTE NE LAISSE EFFACER QUE LES SIENS (elle ajoute `userId` à sa condition pour
 * qui n'est pas organisateur) : un identifiant deviné ne retirerait pas le portrait de
 * quelqu'un d'autre.
 */
export async function removeOwnPortrait(
  domainId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<number> {
  const faces = await fetchFaces(domainId, userId, signal);
  let effaces = 0;
  // Séquentiel : on en compte un ou deux, et chaque suppression touche Rekognition.
  for (const face of faces) {
    if (typeof face.id !== "number") continue;
    await apiFetch(`/v2/domains/${domainId}/faces/${face.id}`, {
      method: "DELETE",
      signal,
    });
    effaces += 1;
  }
  return effaces;
}

export async function searchMyPhotos(
  domainId: number,
  signal?: AbortSignal,
): Promise<SearchOutcome> {
  const answer = await apiFetch<Partial<SearchOutcome> | null>(
    `/v2/domains/${domainId}/faces/search`,
    /* `self` : c'est pour SOI, quel que soit le rôle. Sans lui, un organisateur qui
       posait sa photo était traité comme le back office (tout le domaine, en arrière-
       plan, sans réponse) et son écran concluait « aucune photo » pendant que ses
       photos arrivaient. Vécu le 2026-09-12. */
    { method: "POST", body: { self: true }, signal },
  );
  return {
    done: answer?.done === true,
    created: Number(answer?.created) || 0,
  };
}

/**
 * COMBIEN DE PHOTOS SONT RATTACHÉES À CETTE PERSONNE, tout de suite.
 *
 * Une page d'UNE photo : ce qui nous intéresse est l'en-tête de comptes, pas les
 * images. C'est ce qui rend la surveillance assez légère pour tourner toutes les cinq
 * secondes (voir `album/poll.ts`).
 *
 * Rend `null` quand le serveur ne dit pas les comptes : on ne peut alors rien conclure,
 * et surtout pas « zéro ».
 */
export async function countMyPhotos(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<number | null> {
  const page = await fetchPhotos(domainId, eventId, 1, 1, "mine", signal);
  return page.counts ? page.counts.mine : page.total;
}
